import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import {
  ShieldCheck,
  Users,
  MessageSquare,
  Trash2,
  BadgeCheck,
  Bug,
  Lightbulb,
  MessageCircle,
} from "lucide-react";
import { adminApi, type FeedbackItem } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { formatDate } from "../lib/format";

export default function AdminPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState<"users" | "feedback">("users");

  // Client-side guard (the API also enforces admin; this just avoids a flash).
  if (user && !user.is_admin) return <Navigate to="/" replace />;

  const statsQ = useQuery({ queryKey: ["admin-stats"], queryFn: () => adminApi.stats() });

  const stats = statsQ.data;
  const statCards: { key: string; value: number | undefined }[] = [
    { key: "admin.stats.users", value: stats?.total_users },
    { key: "admin.stats.verified", value: stats?.verified_users },
    { key: "admin.stats.admins", value: stats?.admins },
    { key: "admin.stats.trades", value: stats?.total_trades },
    { key: "admin.stats.newFeedback", value: stats?.new_feedback },
    { key: "admin.stats.signups7d", value: stats?.signups_last_7_days },
  ];

  return (
    <>
      <header>
        <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.admin")}</p>
        <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-accent" />
          {t("admin.title")}
        </h1>
        <p className="text-parchment-muted mt-1">{t("admin.subtitle")}</p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((s) => (
          <div key={s.key} className="card p-4">
            <p className="text-parchment-muted text-[11px] uppercase tracking-wider">{t(s.key)}</p>
            <p className="text-2xl font-bold text-parchment tabular mt-1">{s.value ?? "—"}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <TabButton active={tab === "users"} onClick={() => setTab("users")} icon={Users} label={t("admin.tab.users")} />
        <TabButton
          active={tab === "feedback"}
          onClick={() => setTab("feedback")}
          icon={MessageSquare}
          label={t("admin.tab.feedback")}
          badge={stats?.new_feedback}
        />
      </div>

      {tab === "users" ? <UsersTab currentUserId={user?.id} /> : <FeedbackTab />}
    </>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Users;
  label: string;
  badge?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold border transition " +
        (active ? "text-parchment" : "text-parchment-muted hover:text-parchment")
      }
      style={{
        backgroundColor: active ? "rgb(var(--surface-3))" : "rgb(var(--surface-1))",
        borderColor: active ? "rgb(var(--border-mid))" : "rgb(var(--border-soft))",
      }}
    >
      <Icon className="w-4 h-4" />
      {label}
      {typeof badge === "number" && badge > 0 && (
        <span
          className="ms-1 inline-grid place-items-center min-w-[1.25rem] h-5 px-1 rounded-full text-[11px] font-bold"
          style={{ backgroundColor: "rgb(var(--glow))", color: "#1a1206" }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

// ── Users tab ──────────────────────────────────────────────────────────
function UsersTab({ currentUserId }: { currentUserId?: number }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");

  const usersQ = useQuery({
    queryKey: ["admin-users", search],
    queryFn: () => adminApi.users({ search: search || undefined, per_page: 100 }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const patchM = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: { is_admin?: boolean; email_verified?: boolean } }) =>
      adminApi.updateUser(id, payload),
    onSuccess: invalidate,
    onError: (e: Error) => alert(e.message),
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => adminApi.deleteUser(id),
    onSuccess: invalidate,
    onError: (e: Error) => alert(e.message),
  });

  const rows = usersQ.data?.users ?? [];

  return (
    <div className="card p-0 overflow-hidden">
      <div className="p-4 border-b" style={{ borderColor: "rgb(var(--border-soft))" }}>
        <input
          className="input w-full sm:max-w-xs"
          placeholder={t("admin.users.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {usersQ.isLoading ? (
        <p className="p-6 text-parchment-muted text-sm">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="p-6 text-parchment-muted text-sm">{t("admin.users.empty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-parchment-muted text-xs uppercase tracking-wider" style={{ textAlign: "start" }}>
                <th className="text-start font-semibold px-4 py-2">{t("admin.users.col.user")}</th>
                <th className="text-start font-semibold px-4 py-2">{t("admin.users.col.status")}</th>
                <th className="text-end font-semibold px-4 py-2">{t("admin.users.col.trades")}</th>
                <th className="text-start font-semibold px-4 py-2">{t("admin.users.col.joined")}</th>
                <th className="text-end font-semibold px-4 py-2">{t("admin.users.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const isSelf = u.id === currentUserId;
                return (
                  <tr key={u.id} className="border-t" style={{ borderColor: "rgb(var(--hairline))" }}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-parchment flex items-center gap-2">
                        {u.name || "—"}
                        {isSelf && <span className="chip-gold text-[10px]">{t("admin.users.you")}</span>}
                      </div>
                      <div className="text-parchment-muted text-xs">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {u.is_admin && <span className="chip-gold text-[10px]">{t("admin.users.admin")}</span>}
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-semibold"
                          style={{ color: u.email_verified_at ? "rgb(var(--pnl-pos))" : "rgb(var(--text-muted))" }}
                        >
                          {u.email_verified_at ? t("admin.users.verified") : t("admin.users.unverified")}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-end tabular text-parchment">{u.trades_count}</td>
                    <td className="px-4 py-3 text-parchment-muted">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <button
                          type="button"
                          className="btn-ghost !px-2 !py-1 text-xs"
                          disabled={isSelf && u.is_admin}
                          title={isSelf && u.is_admin ? t("admin.users.you") : undefined}
                          onClick={() => patchM.mutate({ id: u.id, payload: { is_admin: !u.is_admin } })}
                        >
                          <ShieldCheck className="w-3.5 h-3.5" />
                          {u.is_admin ? t("admin.users.revokeAdmin") : t("admin.users.makeAdmin")}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost !px-2 !py-1 text-xs"
                          onClick={() =>
                            patchM.mutate({ id: u.id, payload: { email_verified: !u.email_verified_at } })
                          }
                        >
                          <BadgeCheck className="w-3.5 h-3.5" />
                          {u.email_verified_at ? t("admin.users.unverify") : t("admin.users.verify")}
                        </button>
                        {!isSelf && (
                          <button
                            type="button"
                            className="btn-ghost !px-2 !py-1 text-xs"
                            style={{ color: "rgb(var(--pnl-neg))" }}
                            onClick={() => {
                              if (confirm(t("admin.users.deleteConfirm", { name: u.name || u.email })))
                                deleteM.mutate(u.id);
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Feedback tab ───────────────────────────────────────────────────────
const CAT_ICON: Record<string, typeof Bug> = { bug: Bug, idea: Lightbulb, other: MessageCircle };

function FeedbackTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [status, setStatus] = useState<"" | "new" | "read" | "resolved">("");

  const feedbackQ = useQuery({
    queryKey: ["admin-feedback", status],
    queryFn: () => adminApi.feedback({ status: status || undefined, per_page: 100 }),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-feedback"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };
  const patchM = useMutation({
    mutationFn: ({ id, s }: { id: number; s: "new" | "read" | "resolved" }) => adminApi.updateFeedback(id, s),
    onSuccess: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (id: number) => adminApi.deleteFeedback(id),
    onSuccess: invalidate,
  });

  const items = feedbackQ.data?.feedback ?? [];
  const filters: { value: "" | "new" | "read" | "resolved"; key: string }[] = [
    { value: "", key: "admin.feedback.filter.all" },
    { value: "new", key: "admin.feedback.status.new" },
    { value: "read", key: "admin.feedback.status.read" },
    { value: "resolved", key: "admin.feedback.status.resolved" },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {filters.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setStatus(f.value)}
            className={
              "rounded-lg px-3 py-1.5 text-xs font-semibold border transition " +
              (status === f.value ? "text-parchment" : "text-parchment-muted hover:text-parchment")
            }
            style={{
              backgroundColor: status === f.value ? "rgb(var(--surface-3))" : "rgb(var(--surface-1))",
              borderColor: "rgb(var(--border-soft))",
            }}
          >
            {t(f.key)}
          </button>
        ))}
      </div>

      {feedbackQ.isLoading ? (
        <p className="card p-6 text-parchment-muted text-sm">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <p className="card p-6 text-parchment-muted text-sm">{t("admin.feedback.empty")}</p>
      ) : (
        items.map((f) => <FeedbackCard key={f.id} f={f} onStatus={(s) => patchM.mutate({ id: f.id, s })} onDelete={() => deleteM.mutate(f.id)} />)
      )}
    </div>
  );
}

function FeedbackCard({
  f,
  onStatus,
  onDelete,
}: {
  f: FeedbackItem;
  onStatus: (s: "new" | "read" | "resolved") => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const Icon = CAT_ICON[f.category] ?? MessageCircle;
  const statusColor =
    f.status === "new" ? "rgb(var(--glow))" : f.status === "resolved" ? "rgb(var(--pnl-pos))" : "rgb(var(--text-muted))";

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="grid place-items-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: "rgb(var(--surface-2))" }}
          >
            <Icon className="w-4 h-4 text-accent" />
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-parchment truncate">{f.user_name || f.name || "—"}</p>
            <p className="text-parchment-muted text-xs truncate">{f.user_email || f.email}</p>
          </div>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-wider shrink-0" style={{ color: statusColor }}>
          {t(`admin.feedback.status.${f.status}`)}
        </span>
      </div>

      <p className="text-parchment mt-3 whitespace-pre-wrap break-words">{f.message}</p>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        <p className="text-parchment-muted text-xs">
          {formatDate(f.created_at)}
          {f.url ? ` · ${f.url}` : ""}
        </p>
        <div className="flex gap-1.5">
          {f.status !== "read" && (
            <button type="button" className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onStatus("read")}>
              {t("admin.feedback.markRead")}
            </button>
          )}
          {f.status !== "resolved" ? (
            <button type="button" className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onStatus("resolved")}>
              {t("admin.feedback.markResolved")}
            </button>
          ) : (
            <button type="button" className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onStatus("new")}>
              {t("admin.feedback.reopen")}
            </button>
          )}
          <button
            type="button"
            className="btn-ghost !px-2 !py-1 text-xs"
            style={{ color: "rgb(var(--pnl-neg))" }}
            onClick={() => {
              if (confirm(t("admin.feedback.deleteConfirm"))) onDelete();
            }}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
